import { useState, useEffect } from "react";
import { Confirm, Image, Tab, Grid, Divider, Label } from "semantic-ui-react";
import { RegisteredPayment } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { CustomTable, BasicModal } from "../../../Shared";
import { RegisteredPaymentForm } from "../RegisteredPaymentForm";
import { NewRegisteredPaymentForm } from "../NewRegisteredPaymentForm";
import { StudentDebts } from "../StudentDebts/StudentDebts";
import { buildStorageUrl } from "../../../../utils";
import "./ListRegisteredPayments.scss";

const registeredPaymentController = new RegisteredPayment();

export function ListRegisteredPayments(props) {
  const { studentActive, onReload } = props;
  const { accessToken } = useAuth();

  const [reloadPayment, setReloadPayment] = useState(null);
  const onReloadPayment = () => setReloadPayment((prevState) => !prevState);

  const [registeredPayments, setRegisteredPayments] = useState(null);
  const [registeredPaymentActive, setRegisteredPaymentActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showConfirmDefinitely, setShowConfirmDefinitely] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalReceipt, setShowModalReceipt] = useState(false);

  const onOpenCloseConfirm = (row) => {
    setRegisteredPaymentActive(row);
    setShowConfirm((prevState) => !prevState);
  };

  const onOpenCloseConfirmDefinitely = (row) => {
    setRegisteredPaymentActive(row);
    setShowConfirmDefinitely((prevState) => !prevState);
  };

  const onOpenCloseModal = (row) => {
    setRegisteredPaymentActive(row);
    setShowModal((prevState) => !prevState);
  };

  const onOpenCloseModalReceipt = (row) => {
    setRegisteredPaymentActive(row);
    setShowModalReceipt((prevState) => !prevState);
  };

  const calculateDelayDays = (dueDate, paymentDate) => {
    if (!dueDate) return 0;

    // Si ya se realizó el pago, no hay días de retraso
    if (paymentDate) return 0;

    const today = new Date();
    const due = new Date(dueDate);

    // Normalizar las fechas a medianoche para comparación exacta
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffTime = today - due;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) - 1;

    return diffDays > 0 ? diffDays : 0;
  };

  const getDelayDaysColor = (days) => {
    if (days === 0) return "green";
    if (days <= 7) return "yellow";
    if (days <= 15) return "orange";
    return "red";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "blue";
      case 2:
        return "green";
      case 3:
        return "red";
      case 4:
        return "black";
      case 5:
        return "yellow";
      default:
        return "grey";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return "PENDIENTE";
      case 2:
        return "REALIZADO";
      case 3:
        return "VERIFICADO";
      case 5:
        return "PAGO PARCIAL";
      default:
        return "SIN DEFINIR";
    }
  };

  const getPaymentMethodColor = (paymentMethod) => {
    switch (paymentMethod) {
      case 1:
        return "blue";
      case 2:
        return "purple";
      case 3:
        return "green";
      case 4:
        return "black";
      case 5:
        return "yellow";
      default:
        return "grey";
    }
  };

  const getPaymentMethodText = (paymentMethod) => {
    switch (paymentMethod) {
      case 1:
        return "EFECTIVO";
      case 2:
        return "YAPE";
      case 3:
        return "PLIN";
      case 4:
        return "TRANSFERENCIA BANCARIA";
      case 5:
        return "OTROS";
      default:
        return "SIN DEFINIR";
    }
  };

  const onDelete = async () => {
    try {
      await registeredPaymentController.deleteRegisteredPayment(
        accessToken,
        registeredPaymentActive.id,
      );
      onReloadPayment();
      onOpenCloseConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  const onDeleteDefinitely = async () => {
    try {
      await registeredPaymentController.deleteRegisteredPaymentDefinitely(
        accessToken,
        registeredPaymentActive.id,
      );
      onReloadPayment();
      onOpenCloseConfirmDefinitely();
    } catch (error) {
      console.error(error);
    }
  };

  const tableConfig = {
    headers: [
      "Concepto de pago",
      "Monto",
      // "Código de transacción",
      "Fecha de vencimiento",
      "Días de retraso",
      "Mora",
      "Fecha de pago",
      "Método de pago",
      "Estado",
      "Acciones",
    ],
    fields: [
      {
        field: "PaymentConcept.name",
        dataType: "text",
        align: "center",
      },
      {
        field: "tuitionAmount",
        dataType: "numeric",
        money: true,
        decimal: 2,
        align: "center",
      },
      /* {
        field: "transactionCode",
        dataType: "text",
        align: "center",
      }, */
      {
        field: "PaymentConcept.dueDate",
        dataType: "date",
        format: "DD/MM/YYYY",
        align: "center",
      },
      {
        field: "delayDays",
        component: "Label",
        getText: (value) =>
          value === 0 ? "Sin retraso" : `${value} día${value > 1 ? "s" : ""}`,
        getColor: (value) => getDelayDaysColor(value),
        align: "center",
      },
      {
        field: "penalty",
        component: "Label",
        getColor: (val, row) => row.isLateFeeWaived ? "green" : "grey",
        getText: (val, row) => row.isLateFeeWaived ? "EXONERADA" : `S/. ${Number(val || 0).toFixed(2)}`,
        align: "center",
      },
      {
        field: "paymentDate",
        dataType: "date",
        format: "DD/MM/YYYY",
        align: "center",
      },
      {
        field: "paymentMethod",
        component: "Label",
        getColor: getPaymentMethodColor,
        getText: getPaymentMethodText,
        align: "center",
      },
      {
        field: "status",
        component: "Label",
        getColor: getStatusColor,
        getText: getStatusText,
        align: "center",
      },
      {
        buttons: [
          {
            color: "green",
            icon: "dollar",
            placeholder: "Realizar pago",
            onClick: onOpenCloseModal,
            disabled: "row.status === 2",
          },
          {
            color: "blue",
            icon: "eye",
            placeholder: "Ver comprobante de pago",
            onClick: onOpenCloseModalReceipt,
            disabled: "row.paymentDate === null",
          },
          {
            color: "grey",
            icon: "times",
            placeholder: "Eliminar pago",
            onClick: onOpenCloseConfirm,
            disabled: "row.paymentDate === null",
          },
          {
            color: "red",
            icon: "trash",
            placeholder: "Eliminar concepto de pago",
            onClick: onOpenCloseConfirmDefinitely,
            disabled:
              "row.PaymentConcept.paymentCategoryId === row.Student.paymentCategoryId",
          },
        ],
      },
    ],
    columnWidths: [
      "20%",
      "15%",
      "15%",
      "15%",
      "10%",
      "8%",
      "15%",
      "10%",
      "10%",
      "20%",
    ],
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response =
          await registeredPaymentController.getRegisteredPayments(
            studentActive.id,
            accessToken,
          );

        // Agregar el campo calculado de días de retraso y mora
        const paymentsWithDelayDays = response?.map((payment) => {
          const delayDays = payment.isLateFeeWaived
            ? 0
            : calculateDelayDays(
              payment.PaymentConcept?.dueDate,
              payment.tuitionPaidDate || payment.paymentDate,
            );
          return {
            ...payment,
            delayDays: delayDays,
            penalty: Number(payment.dynamicLateFee !== undefined ? payment.dynamicLateFee : (payment.lateFeeAmount || 0)),
          };
        });

        setRegisteredPayments(paymentsWithDelayDays);
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    })();
  }, [studentActive, reloadPayment, accessToken]);

  const getReceiptPath = () => {
    return buildStorageUrl(registeredPaymentActive?.path);
  };

  const panes = [
    {
      menuItem: { key: "payments", icon: "payment", content: "Conceptos y Pagos" },
      render: () => (
        <Tab.Pane attached={false} className="custom-tab-pane">
          <NewRegisteredPaymentForm
            studentActive={studentActive}
            onReload={onReloadPayment}
          />
          <div className="registered-payment__list">
            <CustomTable
              tableConfig={tableConfig}
              data={registeredPayments}
              onReload={onReload}
              loading={loading}
            />
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: "debts", icon: "hourglass half", content: "Cuentas por Cobrar (CXC)" },
      render: () => (
        <Tab.Pane attached={false} className="custom-tab-pane">
          <StudentDebts studentActive={studentActive} />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <Tab menu={{ secondary: true, pointing: true, className: "custom-payment-tabs" }} panes={panes} />

      <BasicModal
        title={`Realizar pago de ${registeredPaymentActive?.PaymentConcept?.name}`}
        show={showModal}
        size="small"
        close={onOpenCloseModal}
        scrolling={true}
      >
        {registeredPaymentActive && (
          <RegisteredPaymentForm
            registeredPayment={registeredPaymentActive}
            onReload={onReloadPayment}
            onClose={onOpenCloseModal}
          />
        )}
      </BasicModal>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={onDelete}
        content={`¿Eliminar pago de ${registeredPaymentActive?.PaymentConcept?.name}?`}
        size="mini"
      />
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirmDefinitely}
        onCancel={onOpenCloseConfirmDefinitely}
        onConfirm={onDeleteDefinitely}
        content={`¿Eliminar concepto de pago ${registeredPaymentActive?.PaymentConcept?.name}?`}
        size="mini"
      />
      <BasicModal
        title={`Comprobantes de ${registeredPaymentActive?.PaymentConcept?.name}`}
        show={showModalReceipt}
        close={onOpenCloseModalReceipt}
        size="small"
        scrolling={true}
      >
        <div style={{ padding: "1rem" }}>
          {registeredPaymentActive?.debts && registeredPaymentActive.debts.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {registeredPaymentActive.debts.map((debt, index) => (
                <div key={debt.id} style={{ border: "1px solid var(--border-grey-light, #ddd)", padding: "1.2rem", borderRadius: "10px", background: "transparent", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", color: "var(--text-color, inherit)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <h4 style={{ margin: 0 }}>Abono #{index + 1}</h4>
                    <Label size="small" color={debt.status === "APPROVED" || debt.status === "PAID" ? "green" : debt.status === "PENDING" ? "blue" : "red"}>
                      {debt.status === "APPROVED" || debt.status === "PAID" ? "CONFIRMADO" : debt.status === "PENDING" ? "PENDIENTE" : "RECHAZADO"}
                    </Label>
                  </div>
                  {debt.path ? (
                    <Image src={buildStorageUrl(debt.path)} rounded fluid centered style={{ maxHeight: "35vh", objectFit: "contain", marginBottom: "1rem" }} />
                  ) : (
                    <p style={{ fontStyle: "italic", textAlign: "center", margin: "1rem 0" }}>Sin imagen de comprobante</p>
                  )}
                  <Divider style={{ margin: "0.5rem 0" }} />
                  <Grid columns={2} stackable style={{ fontSize: "0.9rem", marginTop: "0.5rem" }}>
                    <Grid.Column><strong>Código Transacción:</strong> {debt.transactionCode || "N/A"}</Grid.Column>
                    <Grid.Column><strong>Fecha de Pago:</strong> {debt.paymentDate ? new Date(debt.paymentDate).toLocaleDateString() : "N/A"}</Grid.Column>
                    <Grid.Column><strong>Monto Cuota:</strong> S/ {Number(debt.tuitionAmount || 0).toFixed(2)}</Grid.Column>
                    <Grid.Column><strong>Monto Mora:</strong> S/ {Number(debt.lateFeeAmount || 0).toFixed(2)}</Grid.Column>
                    <Grid.Column><strong>Total de Abono:</strong> S/ {Number(debt.totalAmount || 0).toFixed(2)}</Grid.Column>
                    {debt.rejectionReason && (
                      <Grid.Column width={16} style={{ color: "red", marginTop: "0.5rem" }}>
                        <strong>Motivo de Rechazo:</strong> {debt.rejectionReason}
                      </Grid.Column>
                    )}
                  </Grid>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              {registeredPaymentActive?.path ? (
                <>
                  <Image src={getReceiptPath()} rounded fluid centered style={{ maxHeight: "60vh", objectFit: "contain", marginBottom: "1rem" }} />
                  <Divider />
                  <Grid columns={2} stackable style={{ marginTop: "0.5rem" }}>
                    <Grid.Column><strong>Código Transacción:</strong> {registeredPaymentActive?.transactionCode || "N/A"}</Grid.Column>
                    <Grid.Column><strong>Monto Total:</strong> S/ {Number(registeredPaymentActive?.totalAmount || 0).toFixed(2)}</Grid.Column>
                  </Grid>
                </>
              ) : (
                <p style={{ padding: "2rem 0", color: "#666" }}>No hay comprobantes registrados.</p>
              )}
            </div>
          )}
        </div>
      </BasicModal>
    </>
  );
}
